import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createBlogPostThunk } from '../store/thunks/blogsThunks';
import RichTextEditor from '../components/editor/RichTextEditor';

const CreateBlogPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { loading, error } = useAppSelector((state) => state.blogs);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate]);

  const categories = [
    'Tutorial',
    'Tips & Tricks',
    'Industry News',
    'Best Practices',
    'Case Study',
    'Review',
    'Announcement',
    'Other',
  ];

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim() || !excerpt.trim() || !category) {
      return;
    }

    if (!user) {
      return;
    }

    setSubmitting(true);
    try {
      const slug = generateSlug(title);
      await dispatch(
        createBlogPostThunk({
          title: title.trim(),
          slug,
          content: content.trim(),
          excerpt: excerpt.trim(),
          authorId: user.uid,
          authorName: user.displayName || user.email || 'Anonymous',
          authorEmail: user.email || '',
          category,
          tags,
          featuredImage: featuredImage.trim() || undefined,
        })
      ).unwrap();

      setSuccess(true);
      setTimeout(() => {
        navigate('/blog');
      }, 2000);
    } catch (err) {
      console.error('Failed to create blog:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Fade in timeout={400}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blog')} sx={{ mb: 4 }}>
          Back to Blog
        </Button>
      </Fade>

      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Create New Blog Post
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Share your knowledge with the community. Your post will be reviewed by an admin before being published.
          </Typography>
        </Box>
      </Fade>

      {success && (
        <Fade in timeout={400}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Blog post created successfully! It's pending admin approval and will be published once approved.
            Redirecting...
          </Alert>
        </Fade>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Fade in timeout={800}>
        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
            border: '1px solid rgba(148, 163, 184, 0.1)',
            borderRadius: 3,
          }}
        >
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(15, 23, 42, 0.5)',
              },
            }}
          />

          <TextField
            fullWidth
            label="Excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            required
            margin="normal"
            multiline
            rows={2}
            helperText="A brief summary of your blog post (will be shown in the blog list)"
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(15, 23, 42, 0.5)',
              },
            }}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
              sx={{
                background: 'rgba(15, 23, 42, 0.5)',
              }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              Content <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Start writing your blog post... Use the toolbar to format your content with headings, lists, links, images, and code blocks."
              helperText="Use the rich text editor to format your content. You can add headings, lists, links, images, code blocks, and more."
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <TextField
              fullWidth
              label="Tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="Press Enter to add a tag"
              helperText="Add tags to help readers find your post"
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(15, 23, 42, 0.5)',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    color: 'primary.light',
                  }}
                />
              ))}
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Featured Image URL (optional)"
            value={featuredImage}
            onChange={(e) => setFeaturedImage(e.target.value)}
            margin="normal"
            placeholder="https://example.com/image.jpg"
            helperText="URL to a featured image for your blog post"
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(15, 23, 42, 0.5)',
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/blog')}
              disabled={submitting}
              sx={{
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: 'text.primary',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={submitting || !title.trim() || !content.trim() || !excerpt.trim() || !category}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Your blog post will be submitted for admin review. Once approved, it will be
              published and visible to all users.
            </Typography>
          </Alert>
        </Paper>
      </Fade>
    </Container>
  );
};

export default CreateBlogPage;

